// Checks remote version.json and reloads if version changes
export const checkRemoteAppVersion = async (): Promise<void> => {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return;
    const json = await res.json();
    const version = json.version;
    if (!version) return;
    const storedVersion = localStorage.getItem('app-version');
    if (storedVersion && storedVersion !== version) {
      // Set version BEFORE clearing caches and reloading to prevent reload loop
      localStorage.setItem('app-version', version);
      await clearAllCaches();
      window.location.reload();
    } else if (!storedVersion) {
      localStorage.setItem('app-version', version);
    }
  } catch (e) {
    // fail silently
  }
};
// Clears all caches (used by PWA update logic)
export const clearAllCaches = async (): Promise<void> => {
  try {
    const cacheNames = await caches.keys();
    const deletePromises = cacheNames.map(cacheName => caches.delete(cacheName));
    await Promise.all(deletePromises);
    console.log('All caches cleared successfully');
  } catch (error) {
    console.error('Error clearing caches:', error);
  }
};

export const clearOldCaches = async (): Promise<void> => {
  try {
    const cacheNames = await caches.keys();
    const oldCaches = cacheNames.filter(name => 
      name.includes('workbox') || 
      name.includes('runtime') || 
      name.includes('precache')
    );
    const deletePromises = oldCaches.map(cacheName => caches.delete(cacheName));
    await Promise.all(deletePromises);
    console.log('Old caches cleared successfully');
  } catch (error) {
    console.error('Error clearing old caches:', error);
  }
};

export const forceRefresh = (): void => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    }).then(() => {
      clearAllCaches().then(() => {
        window.location.reload();
      });
    });
  } else {
    window.location.reload();
  }
};

export const checkForUpdates = async (): Promise<boolean> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
        return true;
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }
  return false;
};

export const setupServiceWorkerUpdate = (): void => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Service worker updated, reload the page
      window.location.reload();
    });

    navigator.serviceWorker.ready.then(registration => {
      // Check for updates every 5 minutes
      setInterval(() => {
        registration.update();
      }, 5 * 60 * 1000);
    });
  }
};

export const handleServiceWorkerError = (): void => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('error', (event) => {
      console.error('Service Worker error:', event);
      // Clear caches and reload on error
      clearAllCaches().then(() => {
        window.location.reload();
      });
    });
  }
};

/**
 * Setup push notification message relay from service worker
 * Relays push messages to the app when in foreground
 */
const setupPushMessageRelay = (): void => {
  try {
    if ('serviceWorker' in navigator) {
      console.log('[Mobile PWA] Setting up push message relay');
      
      // Simple in-memory dedupe to avoid showing duplicate toasts
      const recentPushes = new Map<string, number>();
      const DEDUPE_WINDOW = 5000; // ms
      
      const cleanupDedupe = (now: number) => {
        for (const [k, ts] of recentPushes.entries()) {
          if (now - ts > DEDUPE_WINDOW) recentPushes.delete(k);
        }
      };
      
      // Primary listener: messages via navigator.serviceWorker
      navigator.serviceWorker.addEventListener('message', (event: any) => {
        try {
          const data = event.data;
          if (!data) return;
          
          // Handle notification click navigation
          // Note: Navigation is handled by NotificationNavigationHandler component
          // to avoid full page reload. Just log here for debugging.
          if (data.type === 'NOTIFICATION_CLICKED') {
            const url = data.url || '/';
            const notificationId = data.notificationId;
            console.log('[Mobile PWA] Notification clicked message received:', url, 'ID:', notificationId);
            // Let NotificationNavigationHandler handle the actual navigation
            return;
          }
          
          // Handle push received
          if (data.type === 'PUSH_RECEIVED' || data.type === 'PUSH_DEBUG') {
            const pushId = data.pushId || (data.data && data.data.pushId) || undefined;
            const detail = {
              title: data.title || 'Notification',
              body: data.body || '',
              timestamp: data.timestamp || Date.now(),
              debug: data.type === 'PUSH_DEBUG' ? true : false,
              pushId,
              data: data.data || {}
            };

            // Dedupe logic
            const now = Date.now();
            cleanupDedupe(now);
            const key = pushId ? `pushId::${pushId}` : `${detail.title}::${detail.body}::${Math.floor(detail.timestamp / 1000)}`;
            const last = recentPushes.get(key) || 0;
            if (now - last < DEDUPE_WINDOW) {
              console.log('[Mobile PWA] Duplicate push detected, skipping');
              return;
            }
            recentPushes.set(key, now);

            console.log('[Mobile PWA] Push received, dispatching app-push event:', detail);
            
            try {
              window.dispatchEvent(new CustomEvent('app-push', { detail }));
            } catch (e) {
              (window as any).dispatchEvent && (window as any).dispatchEvent({ type: 'app-push', detail });
            }
            
            // Trigger notification refetch to update bell counter immediately
            try {
              const pushId = data.data && data.data.pushId ? data.data.pushId : undefined;
              console.log('[Mobile PWA] 🔔 Dispatching notification-received event', { pushId });
              window.dispatchEvent(new CustomEvent('notification-received', { detail: { pushId } }));
              console.log('[Mobile PWA] ✅ notification-received event dispatched');
            } catch (e) {
              console.error('[Mobile PWA] Error dispatching notification-received:', e);
            }
          }
        } catch (err) {
          console.error('[Mobile PWA] Message relay failed:', err);
        }
      });

      // Fallback: also listen to window 'message' events
      window.addEventListener('message', (event: MessageEvent) => {
        try {
          const data = event.data;
          if (!data) return;
          
          // Handle notification click navigation
          // Note: Navigation is handled by NotificationNavigationHandler component
          if (data.type === 'NOTIFICATION_CLICKED') {
            const url = data.url || '/';
            const notificationId = data.notificationId;
            console.log('[Mobile PWA] Notification clicked (window message):', url, 'ID:', notificationId);
            // Let NotificationNavigationHandler handle the actual navigation
            return;
          }
          
          // Handle push received
          if (data.type === 'PUSH_RECEIVED' || data.type === 'PUSH_DEBUG') {
            const pushId = data.pushId || (data.data && data.data.pushId) || undefined;
            const detail = {
              title: data.title || 'Notification',
              body: data.body || '',
              timestamp: data.timestamp || Date.now(),
              debug: data.type === 'PUSH_DEBUG' ? true : false,
              pushId,
              data: data.data || {}
            };
            
            const now = Date.now();
            cleanupDedupe(now);
            const key = pushId ? `pushId::${pushId}` : `${detail.title}::${detail.body}::${Math.floor(detail.timestamp / 1000)}`;
            const last = recentPushes.get(key) || 0;
            if (now - last < DEDUPE_WINDOW) {
              return;
            }
            recentPushes.set(key, now);
            
            try {
              window.dispatchEvent(new CustomEvent('app-push', { detail }));
            } catch (e) {
              (window as any).dispatchEvent && (window as any).dispatchEvent({ type: 'app-push', detail });
            }
            
            // Trigger notification refetch
            try {
              const pushId = data.data && data.data.pushId ? data.data.pushId : undefined;
              console.log('[Mobile PWA] 🔔 Dispatching notification-received event (window)', { pushId });
              window.dispatchEvent(new CustomEvent('notification-received', { detail: { pushId } }));
            } catch (e) {
              console.error('[Mobile PWA] Error dispatching notification-received:', e);
            }
          }
        } catch (err) {
          console.error('[Mobile PWA] Window message relay failed:', err);
        }
      });

      // BroadcastChannel fallback
      try {
        const bc = new BroadcastChannel('sw-messages');
        bc.addEventListener('message', (ev: MessageEvent) => {
          try {
            const data = ev.data;
            if (!data) return;
            
            if (data.type === 'PUSH_RECEIVED' || data.type === 'PUSH_DEBUG') {
              const pushId = data.pushId || (data.data && data.data.pushId) || undefined;
              const detail = {
                title: data.title || 'Notification',
                body: data.body || '',
                timestamp: data.timestamp || Date.now(),
                debug: data.type === 'PUSH_DEBUG' ? true : false,
                pushId,
                data: data.data || {}
              };
              
              const now = Date.now();
              cleanupDedupe(now);
              const key = pushId ? `pushId::${pushId}` : `${detail.title}::${detail.body}::${Math.floor(detail.timestamp / 1000)}`;
              const last = recentPushes.get(key) || 0;
              if (now - last < DEDUPE_WINDOW) {
                return;
              }
              recentPushes.set(key, now);
              
              try {
                window.dispatchEvent(new CustomEvent('app-push', { detail }));
              } catch (e) {
                (window as any).dispatchEvent && (window as any).dispatchEvent({ type: 'app-push', detail });
              }
              
              // Trigger notification refetch
              try {
                const pushId = data.data && data.data.pushId ? data.data.pushId : undefined;
                console.log('[Mobile PWA] 🔔 Dispatching notification-received event (BroadcastChannel)', { pushId });
                window.dispatchEvent(new CustomEvent('notification-received', { detail: { pushId } }));
              } catch (e) {
                console.error('[Mobile PWA] BroadcastChannel: Error dispatching notification-received:', e);
              }
            }
          } catch (err) {
            console.error('[Mobile PWA] BroadcastChannel handler failed:', err);
          }
        });
      } catch (e) {
        // BroadcastChannel may be unavailable in some environments; ignore.
      }
      
      console.log('[Mobile PWA] Push message relay setup complete');
    }
  } catch (err) {
    console.error('[Mobile PWA] Setup message relay error:', err);
  }
};

// Initialize PWA utilities
export const initializePWA = (): void => {
  checkRemoteAppVersion();
  setupServiceWorkerUpdate();
  handleServiceWorkerError();
  // Clear old caches on app start
  clearOldCaches();
  // Setup push notification message relay
  setupPushMessageRelay();
};
